import { useContext, useEffect, useState } from 'react';
import { Alert, Descriptions, Flex, Input, Modal, Select, Spin, Table, Tag, Typography, message } from 'antd';
import AuthenticationContext from '../../../contexts/auth/AuthenticationContext';
import UserEditionFormContext from '../../../contexts/forms/users/UserEditionFormContext';
import UserDoc from '../../../../domain/models/UserDoc';
import previewUserCallerId, { CallerIdPreview, CallerIdPreviewFile, CallrDocumentType } from '../../../../domain/use_cases/users/previewUserCallerId';
import requestUserCallerId, { RequestUserCallerIdPayload } from '../../../../domain/use_cases/users/requestUserCallerId';
import getUserById from '../../../../domain/use_cases/users/getUserById';

// Nom du tiers Callr : 32 caractères max
const CALLR_NAME_MAX = 32;

// Types de document de la console Callr
const CALLR_TYPE_OPTIONS: { value: CallrDocumentType | 'NONE'; label: string }[] = [
    { value: 'COMPANY_REG', label: 'KBIS / immatriculation' },
    { value: 'POA', label: 'Facture / justificatif de domicile' },
    { value: 'MANDATE', label: 'Mandat' },
    { value: 'ID', label: "Pièce d'identité" },
    { value: 'OTHER', label: 'Autre document' },
    { value: 'NONE', label: 'Ne pas envoyer' },
];

interface UserCallerIdModalProps {
    open: boolean;
    onCancel: () => void;
    userDoc: UserDoc;
}

export default function UserCallerIdModal({ open, onCancel, userDoc }: UserCallerIdModalProps) {
    const authContext = useContext(AuthenticationContext);
    const userEditionFormContext = useContext(UserEditionFormContext);

    const [preview, setPreview] = useState<CallerIdPreview | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    // Type choisi par fichier : celui de l'IA par défaut, corrigeable à la main
    const [types, setTypes] = useState<Record<string, CallrDocumentType | 'NONE'>>({});
    // Nom chez Callr et note : ceux du serveur par défaut, modifiables au besoin
    const [name, setName] = useState('');
    const [note, setNote] = useState('');

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        // Reset avant l'appel réseau qui remplit l'aperçu
        // eslint-disable-next-line react/set-state-in-effect
        setPreview(null);
        setError(null);
        setLoading(true);
        previewUserCallerId(userDoc.uid, authContext.authentication)
            .then((result) => {
                if (cancelled) return;
                setPreview(result);
                setName(result.name);
                setNote(result.note);
                setTypes(Object.fromEntries(result.files.map((f) => [f.id, f.send && f.type ? f.type : 'NONE'])));
            })
            .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Analyse des documents impossible'))
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, [open, userDoc.uid]);

    const selected = Object.entries(types)
        .filter(([, type]) => type !== 'NONE')
        .map(([id, type]) => ({ id, type: type as CallrDocumentType }));
    const blocked = !preview || preview.blockers.length > 0 || selected.length === 0 || !name.trim() || !note.trim();

    const handleSend = async () => {
        setSending(true);
        try {
            const result = await requestUserCallerId(userDoc.uid, new RequestUserCallerIdPayload(selected, name.trim(), note.trim()), authContext.authentication);
            message.success(`Caller ID demandé (${result.state}), ${result.documents.length} document(s) chez Callr`);
            // La note a été ajoutée côté serveur : on recharge le user pour que l'onglet Informations ne l'écrase pas
            const freshUser = await getUserById(userDoc.uid);
            if (freshUser) userEditionFormContext.initWithUser(freshUser);
            onCancel();
        } catch (e) {
            message.error(e instanceof Error ? e.message : 'Demande de caller ID impossible');
        } finally {
            setSending(false);
        }
    };

    const columns = [
        {
            title: 'Fichier',
            key: 'name',
            render: (file: CallerIdPreviewFile) => (
                <Flex vertical>
                    <Typography.Text strong>{file.name}</Typography.Text>
                    <Typography.Text type="secondary">{file.description}</Typography.Text>
                    {(file.person_names.length > 0 || file.company_names.length > 0 || file.phone_numbers.length > 0) && (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            {[...file.person_names, ...file.company_names].join(' · ')}
                            {file.phone_numbers.length > 0 && ` · Tél : ${file.phone_numbers.join(', ')}`}
                        </Typography.Text>
                    )}
                </Flex>
            ),
        },
        {
            title: 'Vérification',
            key: 'reason',
            width: 220,
            render: (file: CallerIdPreviewFile) => <Tag color={file.send ? 'green' : 'red'}>{file.reason}</Tag>,
        },
        {
            title: 'Type Callr',
            key: 'type',
            width: 260,
            render: (file: CallerIdPreviewFile) => (
                <Select
                    style={{ width: '100%' }}
                    value={types[file.id]}
                    options={CALLR_TYPE_OPTIONS}
                    onChange={(value) => setTypes((prev) => ({ ...prev, [file.id]: value }))}
                />
            ),
        },
    ];

    return <Modal
        title="Demande d'affichage du n° chez Callr"
        open={open}
        onOk={handleSend}
        onCancel={onCancel}
        okText="Envoyer la demande"
        cancelText="Annuler"
        confirmLoading={sending}
        okButtonProps={{ disabled: blocked }}
        width={1000}
        destroyOnHidden
    >
        {loading && (
            <Flex vertical align="center" gap="small" style={{ padding: 32 }}>
                <Spin />
                <Typography.Text type="secondary">Lecture des documents par l'IA, environ 30 secondes...</Typography.Text>
            </Flex>
        )}

        {error && <Alert type="error" showIcon message={error} />}

        {preview && (
            <Flex vertical gap="middle">
                <Descriptions size="small" column={1} bordered items={[
                    {
                        key: 'name',
                        label: 'Nom chez Callr',
                        children: <Input value={name} maxLength={CALLR_NAME_MAX} showCount onChange={(e) => setName(e.target.value)} status={name.trim() ? undefined : 'error'} />,
                    },
                    { key: 'phone', label: 'Numéro', children: preview.phone || '-' },
                    {
                        key: 'note',
                        label: 'Note ajoutée',
                        children: <Input value={note} onChange={(e) => setNote(e.target.value)} status={note.trim() ? undefined : 'error'} />,
                    },
                ]} />

                {preview.existingCallerId && preview.blockers.length === 0 && (
                    <Alert type="info" showIcon message={`Demande déjà commencée (${preview.existingCallerId.state}) : seuls les documents manquants seront envoyés.`} />
                )}

                {preview.blockers.map((blocker) => <Alert key={blocker} type="error" showIcon message={blocker} />)}

                {preview.blockers.length === 0 && selected.length === 0 && (
                    <Alert type="warning" showIcon message="Aucun document à envoyer : pas de demande possible." />
                )}

                <Table
                    size="small"
                    pagination={false}
                    columns={columns}
                    rowKey={(file) => file.id}
                    dataSource={preview.files}
                />
            </Flex>
        )}
    </Modal>;
}
